const CARRIER_SERVICE_URL = 'https://tptoys.qfconnector.com/api/carrier-service';

function buildDeliveryMessageFromRates(result) {
  const rates = result?.rates || [];
  let nextDay = '';
  let deliveryMessageContent = '';

  if (rates.length > 0) {
    let earliestDate = null;

    rates.forEach((availableDate) => {
      const datePart = availableDate.service_code?.split('|')?.[1];
      if (!datePart) return;
      const dateParts = datePart.split('-');
      const date = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);

      if (earliestDate === null || date.getTime() < earliestDate.getTime()) {
        earliestDate = date;
        const d = date.getDate();
        const m = date.getMonth() + 1;
        const y = date.getFullYear();
        nextDay = `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
        deliveryMessageContent = availableDate.description || '';
      }
    });
  } else if (rates[0]) {
    deliveryMessageContent = rates[0].service_name || '';
  }

  return nextDay ? nextDay.trim() : deliveryMessageContent;
}

class DeliveryData extends HTMLElement {
  static get STORAGE_KEY() {
    return 'tp-delivery-data';
  }

  static _fetchPromise = null;

  static get observedAttributes() {
    return ['src', 'date', 'product-id', 'product-price', 'product-sku'];
  }

  constructor() {
    super();
    this._onDeliveryDataUpdated = this._onDeliveryDataUpdated.bind(this);
    this.dateSlot = this.querySelector('#date');
  }

  _getProductAttrs() {
    const productId = this.getAttribute('product-id') || this.dataset.productId;
    const productPrice = this.getAttribute('product-price') || this.dataset.productPrice;
    const productSku = this.getAttribute('product-sku') || this.dataset.productSku;
    return { productId, productPrice, productSku };
  }

  static getItemsFromFirstElements(limit = 3) {
    const elements = document.querySelectorAll('delivery-data');
    const firstN = Array.from(elements).slice(0, limit);
    return firstN
      .map((el) => {
        const productId = el.getAttribute('product-id') || el.dataset.productId;
        const productPrice = el.getAttribute('product-price') || el.dataset.productPrice;
        const productSku = el.getAttribute('product-sku') || el.dataset.productSku;
        return { productId, productPrice, productSku };
      })
      .filter((item) => item.productId && item.productPrice && item.productSku)
      .map(({ productId, productPrice, productSku }) => ({
        product_id: productId,
        requires_shipping: true,
        quantity: 1,
        price: productPrice,
        sku: productSku
      }));
  }

  async connectedCallback() {
    const key = DeliveryData.STORAGE_KEY;
    let data = sessionStorage.getItem(key);

    if (!data) {
      this.dateSlot.setAttribute('data-loading', 'true');
      try {
        if (DeliveryData._fetchPromise) {
          data = await DeliveryData._fetchPromise;
        } else {
          const url = this.getAttribute('src') || CARRIER_SERVICE_URL;
          const items = DeliveryData.getItemsFromFirstElements(3);

          if (items.length === 0) {
            console.warn('<delivery-data> no valid product-id, product-price or product-sku on first 3 elements');
            this.render();
            return;
          }

          DeliveryData._fetchPromise = (async () => {
            const body = JSON.stringify({
              rate: {
                destination: {
                  country: 'UK',
                  postal_code: 'MK45 5BX ',
                  city: 'Flitwick',
                  address1: '11 Enterprise Way',
                  phone: '01525722722',
                  company_name: 'Mookie Toys'
                },
                items,
                currency: 'GBP',
                locale: 'en'
              }
            });

            const res = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body,
              redirect: 'follow'
            });

            const responseText = await res.text();
            if (!res.ok) {
              throw new Error(`Carrier service error: ${res.status} ${res.statusText}`);
            }

            const result = JSON.parse(responseText);
            const message = buildDeliveryMessageFromRates(result);
            sessionStorage.setItem(key, message);
            window.dispatchEvent(new CustomEvent('delivery-data-updated', { detail: message }));
            return message;
          })();

          data = await DeliveryData._fetchPromise;
          DeliveryData._fetchPromise = null;
        }
      } catch (err) {
        DeliveryData._fetchPromise = null;
        console.error('<delivery-data> fetch failed:', err);
        this.render();
        return;
      }

      this.dateSlot.setAttribute('data-loading', 'false');
    }

    this._deliveryData = data;
    this.setAttribute('date', data);
    window.addEventListener('delivery-data-updated', this._onDeliveryDataUpdated);
  }

  _onDeliveryDataUpdated(event) {
    this._deliveryData = event.detail;
    this.setAttribute('date', event.detail);
  }

  disconnectedCallback() {
    window.removeEventListener('delivery-data-updated', this._onDeliveryDataUpdated);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    this.render();
  }

  render() {
    if (this.dateSlot) {
      this.dateSlot.textContent = this._deliveryData;
      this.dateSlot.setAttribute('data-loading', 'false');
    }
  }
}

customElements.define('delivery-data', DeliveryData);
